(() => {
if (customElements.get('wildgainz-modal-trigger')) return;
/* Wild Gainz shared modal opener.
   Progressive enhancement over native <dialog>: triggers are links that
   navigate to a fallback URL without JS; with JS they open the dialog.

   Usage:
     <wildgainz-modal-trigger data-modal="Dialog-{{ section.id }}">
       <a href="{{ fallback_url }}" class="wg-btn">Label</a>
     </wildgainz-modal-trigger>
     <dialog id="Dialog-{{ section.id }}" class="wg-modal">
       <button class="wg-modal__close" data-modal-close aria-label="Close">✕</button>
       ...
     </dialog>

   Other scripts open a dialog with window.WildgainzModal.open(dialog) so
   every popup gets the same treatment.
*/

const LOCK_CLASS = 'wg-modal-open';

/* Open a dialog without letting the page behind it move.

   A dialog is rendered inside its section. While it is open, every click
   in it counts as a click inside that section (the theme editor then
   selects the section and scrolls the preview to it) and Safari can scroll
   the page to the section when a field inside gets focus. So the dialog is
   lifted into <body> for as long as it is open and put back on close; the
   page scroll is locked meanwhile and restored afterwards. */
function open(dialog) {
  if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
  if (dialog.parentElement !== document.body) {
    // Re-create the placeholder whenever the old one has left the document:
    // the theme editor can re-render the section and drop a comment node that
    // is not in the server HTML, and a stale home would later be read as
    // "this copy is orphaned" and delete the only dialog there is.
    if (!dialog.__wgHome || !dialog.__wgHome.isConnected) {
      dialog.__wgHome = document.createComment('wg-modal-home');
      dialog.parentElement.insertBefore(dialog.__wgHome, dialog);
    }
    document.body.appendChild(dialog);
  }
  dialog.__wgScroll = [window.scrollX, window.scrollY];
  document.documentElement.classList.add(LOCK_CLASS);
  dialog.addEventListener('close', onClose, { once: true });
  dialog.showModal();
  window.scrollTo(dialog.__wgScroll[0], dialog.__wgScroll[1]);
}

function onClose(event) {
  const dialog = event.target;
  // close() drops the dialog from the top layer straight away but queues this
  // event, so it can have been re-opened in the meantime. Put the listener
  // back and leave it alone.
  if (dialog.open) {
    dialog.addEventListener('close', onClose, { once: true });
    return;
  }
  // Another popup may still be open, and it needs the lock kept.
  if (!document.querySelector('dialog.wg-modal[open]')) {
    document.documentElement.classList.remove(LOCK_CLASS);
  }
  const home = dialog.__wgHome;
  if (home && home.isConnected) {
    home.parentElement.insertBefore(dialog, home);
  } else if (home) {
    // The section re-rendered while the dialog was open (theme editor) and
    // now holds a fresh copy; this one is stale.
    dialog.remove();
  }
  const at = dialog.__wgScroll || [window.scrollX, window.scrollY];
  window.scrollTo(at[0], at[1]);
  requestAnimationFrame(() => window.scrollTo(at[0], at[1]));
}

window.WildgainzModal = { open };

class WildgainzModalTrigger extends HTMLElement {
  connectedCallback() {
    if (this.__wgBound) return;
    this.__wgBound = true;
    this.addEventListener('click', (event) => {
      const dialog = this.findDialog();
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      this.selectTab(dialog);
      open(dialog);
    });
  }

  selectTab(dialog) {
    // data-lab-key opens a lab results dialog on a specific flavor tab, so a
    // trigger sitting on one flavor's card lands on that flavor's report.
    // Selected through the tabs component, never a synthetic click: a click
    // inside another section makes the theme editor select and scroll to it.
    const key = this.dataset.labKey;
    if (!key) return;
    const escaped = window.CSS && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
    const tab = dialog.querySelector('[data-lab-tab][data-lab-key="' + escaped + '"]');
    if (!tab) return;
    const comp = tab.closest('wildgainz-lab-results');
    const idx = comp && comp.tabs ? comp.tabs.indexOf(tab) : -1;
    if (comp && typeof comp.select === 'function' && idx > -1) comp.select(idx);
    else tab.click();
  }

  findDialog() {
    // data-modal targets a dialog id within the same section. data-modal-name
    // finds a named dialog anywhere on the page, so a button in one section
    // can open a popup owned by another. Without a match the wrapped link
    // navigates normally.
    if (this.dataset.modal) return document.getElementById(this.dataset.modal);
    const name = this.dataset.modalName;
    if (!name) return null;
    const escaped = window.CSS && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '\\"');
    return document.querySelector('dialog[data-wg-modal-name="' + escaped + '"]');
  }
}

if (!customElements.get('wildgainz-modal-trigger')) {
  customElements.define('wildgainz-modal-trigger', WildgainzModalTrigger);
}

document.addEventListener('click', (event) => {
  const closer = event.target.closest('[data-modal-close]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }
  const dialog = event.target.closest('dialog.wg-modal');
  if (!dialog || event.target !== dialog) return;
  // The dialog is the event target both for a click on ::backdrop and for one
  // on its own padding, so hit-test the box before dismissing. Keyboard
  // activation reports detail 0 and never lands here.
  if (event.detail === 0) return;
  const box = dialog.getBoundingClientRect();
  const inside =
    event.clientX >= box.left &&
    event.clientX <= box.right &&
    event.clientY >= box.top &&
    event.clientY <= box.bottom;
  if (!inside) dialog.close();
});
})();
